import { useMemo } from "react";
import {
  Calendar,
  dateFnsLocalizer,
  Views,
  type ToolbarProps,
} from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { ChevronLeftIcon, ChevronRightIcon } from "./icons";
import "react-big-calendar/lib/css/react-big-calendar.css";

export interface CalendarTask {
  _id: string;
  title: string;
  status: "requested" | "scheduled" | "completed" | "cancelled";
  scheduledFor: string | null;
  contractor: { name: string; serviceType: string } | null;
  unit: { label: string } | null;
}

export interface CalendarAvailability {
  _id: string;
  type: "blackout" | "preferred";
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  unit: { _id: string; label: string } | null;
  notes: string | null;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource:
    | { kind: "task"; status: CalendarTask["status"] }
    | { kind: "availability"; type: CalendarAvailability["type"] };
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { "en-US": enUS },
});

const eventStyles: Record<CalendarTask["status"], React.CSSProperties> = {
  scheduled: {
    backgroundColor: "#dbeafe",
    color: "#1e3a8a",
    border: "1px solid #bfdbfe",
  },
  requested: {
    backgroundColor: "#fef3c7",
    color: "#78350f",
    border: "1px solid #fde68a",
  },
  completed: {
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "1px solid #e2e8f0",
  },
  cancelled: {
    backgroundColor: "#f1f5f9",
    color: "#94a3b8",
    border: "1px solid #e2e8f0",
  },
};

const availabilityStyles: Record<
  CalendarAvailability["type"],
  React.CSSProperties
> = {
  blackout: {
    backgroundColor: "rgba(244, 63, 94, 0.12)",
    border: "1px dashed rgba(244, 63, 94, 0.4)",
    // rbc's default event color is white — set a legible dark text
    // color so the title reads against the faint rose background.
    color: "#9f1239",
  },
  preferred: {
    backgroundColor: "rgba(16, 185, 129, 0.12)",
    border: "1px dashed rgba(16, 185, 129, 0.4)",
    color: "#065f46",
  },
};

export function CalendarView({
  tasks,
  availability = [],
  onEventClick,
  onSlotSelect,
}: {
  tasks: CalendarTask[];
  availability?: CalendarAvailability[];
  onEventClick?: (taskId: string) => void;
  // Fires when the user clicks an empty date / drags a date range.
  // start is inclusive, end is exclusive (react-big-calendar
  // convention), both in local browser time.
  onSlotSelect?: (range: { start: Date; end: Date }) => void;
}) {
  const events = useMemo<CalendarEvent[]>(
    () =>
      tasks
        .filter((t): t is CalendarTask & { scheduledFor: string } =>
          t.scheduledFor !== null,
        )
        .map((t) => {
          const start = new Date(t.scheduledFor);
          const end = new Date(start.getTime() + 60 * 60 * 1000);
          return {
            id: t._id,
            title: t.title,
            start,
            end,
            resource: { kind: "task" as const, status: t.status },
          };
        }),
    [tasks],
  );

  // react-big-calendar's `backgroundEvents` prop is only consumed by
  // TimeGrid (week/day views) — Month view silently drops it. We're
  // month-only here, so availability has to ride the regular `events`
  // array. The eventPropGetter differentiates by `resource.kind` so
  // styling is preserved.
  const availabilityEvents = useMemo<CalendarEvent[]>(
    () =>
      availability.map((a) => ({
        id: a._id,
        title: `${a.type === "blackout" ? "Blackout" : "Preferred"}${
          a.unit ? ` · ${a.unit.label}` : ""
        }${a.notes ? ` · ${a.notes}` : ""}`,
        // YYYY-MM-DD parses as midnight local; pad end to make the
        // last day inclusive in the calendar's day cells.
        start: new Date(`${a.startDate}T00:00:00`),
        end: new Date(`${a.endDate}T23:59:59`),
        resource: { kind: "availability" as const, type: a.type },
      })),
    [availability],
  );

  const allEvents = useMemo(
    () => [...events, ...availabilityEvents],
    [events, availabilityEvents],
  );

  const unscheduledCount = useMemo(
    () => tasks.filter((t) => t.scheduledFor === null).length,
    [tasks],
  );

  return (
    <div className="px-6 py-4">
      <Legend hasAvailability={availability.length > 0} />
      {unscheduledCount > 0 && (
        <p className="mb-3 text-xs text-slate-500">
          {unscheduledCount} {unscheduledCount === 1 ? "task" : "tasks"}{" "}
          awaiting a date — see the list view.
        </p>
      )}
      {/*
        Compact month-only overview calendar — day cells convey
        "something is here" at a glance; tap a day or use list view
        for the actual content.
      */}
      <div style={{ height: 405 }}>
        <Calendar<CalendarEvent>
          localizer={localizer}
          events={allEvents}
          startAccessor="start"
          endAccessor="end"
          defaultView={Views.MONTH}
          views={[Views.MONTH]}
          components={{ toolbar: CompactToolbar }}
          selectable={Boolean(onSlotSelect)}
          onSelectEvent={(event) => {
            if (event.resource.kind === "task") onEventClick?.(event.id);
          }}
          onSelectSlot={(slotInfo) =>
            onSlotSelect?.({ start: slotInfo.start, end: slotInfo.end })
          }
          eventPropGetter={(event) =>
            event.resource.kind === "task"
              ? { style: eventStyles[event.resource.status] }
              : { style: availabilityStyles[event.resource.type] }
          }
        />
      </div>
    </div>
  );
}

function Legend({ hasAvailability }: { hasAvailability: boolean }) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-500">
      <Chip style={eventStyles.scheduled}>Scheduled</Chip>
      <Chip style={eventStyles.requested}>Requested</Chip>
      <Chip style={eventStyles.completed}>Completed</Chip>
      <Chip style={eventStyles.cancelled}>Cancelled</Chip>
      {hasAvailability && (
        <>
          <span className="text-slate-300">|</span>
          <Chip style={availabilityStyles.blackout} dashed>
            Blackout
          </Chip>
          <Chip style={availabilityStyles.preferred} dashed>
            Preferred
          </Chip>
        </>
      )}
    </div>
  );
}

// Strips the default react-big-calendar toolbar (Today / Back / Next
// / view-switcher) down to just chevron-arrows around the month
// label — we only show month view, no need for the other controls.
function CompactToolbar({ label, onNavigate }: ToolbarProps<CalendarEvent>) {
  return (
    <div className="flex items-center justify-center gap-4 py-2.5">
      <button
        type="button"
        onClick={() => onNavigate("PREV")}
        aria-label="Previous month"
        className="p-1 text-slate-400 hover:text-slate-900 transition-colors"
      >
        <ChevronLeftIcon />
      </button>
      <span className="text-sm font-medium text-slate-900 min-w-[120px] text-center tabular-nums">
        {label}
      </span>
      <button
        type="button"
        onClick={() => onNavigate("NEXT")}
        aria-label="Next month"
        className="p-1 text-slate-400 hover:text-slate-900 transition-colors"
      >
        <ChevronRightIcon />
      </button>
    </div>
  );
}

function Chip({
  style,
  dashed,
  children,
}: {
  style: React.CSSProperties;
  dashed?: boolean;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className="inline-block h-2.5 w-4 rounded-sm"
        style={{
          backgroundColor: style.backgroundColor as string,
          border: dashed
            ? (style.border as string)
            : (style.border as string),
        }}
      />
      {children}
    </span>
  );
}
